import React, { useMemo } from 'react';
import { FormControl, FormHelperText, InputLabel, Box, Typography, TextField, Autocomplete, Paper } from '@mui/material';

// Style for the country flag
const countryFlagStyle = {
  marginRight: '8px',
  fontSize: '1.2em',
  width: '20px'
};

// Get flag emoji from country code
const getFlagEmoji = (countryCode) => {
  if (!countryCode) return '';
  
  // Special case for IL - show Palestine flag instead
  if (countryCode.toUpperCase() === 'IL') {
    // Use PS country code for Palestine flag
    const palestineCode = 'PS';
    const codePoints = palestineCode
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  }
  
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

// Common country list - limited to commonly used countries for performance
// Egypt is first in the list for prominence
const COUNTRIES = [
  { code: 'EG', name: 'Egypt' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'QA', name: 'Qatar' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'OM', name: 'Oman' },
  { code: 'JO', name: 'Jordan' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'SY', name: 'Syria' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'PS', name: 'Palestine' },
  { code: 'YE', name: 'Yemen' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'TR', name: 'Turkey' },
  { code: 'IN', name: 'India' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'CN', name: 'China' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'PT', name: 'Portugal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'RU', name: 'Russia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IL', name: 'Palestine ❤️' }
];

const CountrySelect = ({ 
  value = 'EG',  // Default to Egypt
  onChange, 
  label, 
  error, 
  helperText, 
  required, 
  disabled,
  placeholder
}) => {
  // Add flags to countries
  const countriesWithFlags = useMemo(() => {
    return COUNTRIES.map(country => ({
      ...country,
      flag: getFlagEmoji(country.code)
    }));
  }, []);

  // Find selected country
  const selectedCountry = useMemo(() => {
    return countriesWithFlags.find(country => country.code === value) || 
           countriesWithFlags.find(country => country.code === 'EG'); // Default to Egypt
  }, [value, countriesWithFlags]);

  return (
    <FormControl fullWidth error={error} required={required} disabled={disabled}>
      <Autocomplete
        value={selectedCountry}
        onChange={(event, newValue) => {
          onChange(newValue?.code || 'EG');
        }}
        componentsProps={{
          popper: {
            sx: { zIndex: 1300 }
          }
        }}
        options={countriesWithFlags}
        getOptionLabel={(option) => option.name}
        size="medium"
        sx={{ 
          '& .MuiOutlinedInput-root': {
            height: '47px'  // Match standard height of other inputs
          },
          '& .MuiInputBase-input': {
            padding: '6px 14px',
            display: 'flex',
            alignItems: 'center'
          },
          '& .MuiAutocomplete-endAdornment': {
            top: '50%',
            transform: 'translateY(-50%)',
            right: '9px'
          }
        }}
        renderInput={(params) => (
          <TextField 
            {...params} 
            label={label}
            error={error}
            required={required}
            placeholder={placeholder || "Search countries"}
            InputProps={{
              ...params.InputProps,
              startAdornment: selectedCountry ? (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <span style={{ ...countryFlagStyle, marginLeft: '4px' }}>{selectedCountry.flag}</span>
                  {params.InputProps.startAdornment}
                </Box>
              ) : params.InputProps.startAdornment
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" sx={{ display: 'flex', alignItems: 'center', p: 1 }} {...props}>
            <span style={countryFlagStyle}>{option.flag}</span>
            <Typography variant="body1">{option.name}</Typography>
          </Box>
        )}
        PaperComponent={(props) => (
          <Paper 
            {...props} 
            elevation={3} 
            sx={{ 
              maxHeight: 300, 
              overflowY: 'auto',
              '& .MuiAutocomplete-listbox': {
                padding: 0,
              }
            }}
          />
        )}
        filterOptions={(options, { inputValue }) => {
          const filterValue = inputValue.toLowerCase().trim();
          if (!filterValue) return options;
          
          return options.filter(option => 
            option.name.toLowerCase().includes(filterValue) || 
            option.code.toLowerCase().includes(filterValue)
          );
        }}
        disableClearable
        autoHighlight
        openOnFocus
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default CountrySelect;